import generalConstants from 'constants/general';
import fileTypes from 'constants/file-types';

const imageTypes = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/svg',
  'image/svg+xml',
  'image/webp'
];
const videoTypes = [
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/AV1',
  'video/H264',
  'video/quicktime'
];
const csvType = 'text/csv';
export const xlsxTypes = [
  'application/vnd.openxmlformats',
  'officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];
const documentTypes = [
  'application/pdf',
  csvType,
  'text/plain',
  'text/html',
  'application/msword',
  'application/vnd.openxmlformats',
  'officedocument.wordprocessingml.document'
];

class DOMUtility {
  constructor() {
    this.imageType = imageTypes.join(',');
    this.videoType = videoTypes.join(',');
    this.audioType = 'audio/*';
    this.documentType = documentTypes.join(',');
    this.csvType = csvType;

    // Minimal mime → common extensions mapping for FS API (not exhaustive).
    this._mimeToExt = {
      // images
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'image/svg+xml': ['.svg'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'],

      // video (codecs like AV1/H264 are NOT MIME types; skip them)
      'video/mp4': ['.mp4'],
      'video/webm': ['.webm'],
      'video/ogg': ['.ogv', '.ogg'],
      'video/quicktime': ['.mov'],
      'video/*': ['.mp4', '.webm', '.ogv', '.mov'],

      // audio
      'audio/*': ['.mp3', '.wav', '.ogg', '.m4a', '.flac'],

      // docs
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'text/html': ['.html', '.htm'],
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    };

  }

  handleFileInputChange() {}

  _normalizeMime(token) {
    const t = token.toLowerCase().trim();

    // Drop obvious garbage quickly
    if (!t) return null;

    // Fix common aliases
    if (t === 'image/jpg') return 'image/jpeg';
    if (t === 'image/svg') return 'image/svg+xml';

    // Office “aliases” from your lists
    if (t === 'officedocument.spreadsheetml.sheet')
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (t === 'officedocument.wordprocessingml.document')
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    // Broad “application/vnd.openxmlformats” → include both docx/xlsx by returning a marker array
    if (t === 'application/vnd.openxmlformats')
      return ['application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

    // Codec names are not MIME types — skip them
    if (t === 'video/av1' || t === 'video/h264') return null;

    // Allow standard mimes and wildcards present in _mimeToExt
    return t;
  }

  _buildPickerTypesFromAccept(acceptString) {
    if (!acceptString || typeof acceptString !== 'string') return [];

    const tokens = acceptString.split(',').map(s => s.trim()).filter(Boolean);

    // Expand aliases that map to multiple real mimes
    const mimes = [];
    for (const tok of tokens) {
      const norm = this._normalizeMime(tok);
      if (!norm) continue;
      if (Array.isArray(norm)) mimes.push(...norm);
      else mimes.push(norm);
    }

    // Group into one “type” to mirror single accept string behavior
    const acceptMap = {};
    for (const mime of mimes) {
      const exts = this._mimeToExt[mime];
      if (!exts || !exts.length) continue; // skip unknowns instead of inserting invalid entries
      // de-duplicate extensions
      acceptMap[mime] = Array.from(new Set(exts));
    }

    // If nothing valid, return [] so we omit `types` (picker shows all); fallback still applies on catch.
    const hasAny = Object.keys(acceptMap).length > 0;
    return hasAny ? [{ description: acceptString, accept: acceptMap }] : [];
  }

  // ---------- Public API kept the same ----------
  askFile(options = {}) {
    const accept = options.type || '';

    // Try FS Access API first with converted options; fallback on failure.
    if (window.showOpenFilePicker) {
      return this._askFileViaFilePicker(accept).catch(() => this._askFileViaInput(accept));
    }
    return this._askFileViaInput(accept);
  }

  askFiles(options = {}) {
    const accept = options.type || '';
    const limit = options.limit || null;

    if (window.showOpenFilePicker) {
      return this._askFilesViaFilePicker(accept).catch(() => this._askFileViaInput(accept, { multiple: true })).then(function applyLimit(result) {
        return this._applyFilesLimit(result, limit);
      }.bind(this));
    }
    return this._askFileViaInput(accept, { multiple: true }).then(function applyLimit(result) {
      return this._applyFilesLimit(result, limit);
    }.bind(this));
  }

  _applyFilesLimit(result, limit) {
    if (result.cancelled === true) return result;
    const maxLimit = 10;
    const effectiveLimit = limit === null ? 1 : Math.min(limit, maxLimit);
    return { files: result.files.slice(0, effectiveLimit) };
  }

  // ---------- Private: FS Access API path with input-compat conversion ----------
  _askFileViaFilePicker(accept) {
    const pickerTypes = this._buildPickerTypesFromAccept(accept);

    return window.showOpenFilePicker({
        multiple: false,
        types: pickerTypes.length ? pickerTypes : undefined,
        excludeAcceptAll: false
      })
      .then(async ([handle]) => ({ file: await handle.getFile() }))
      .catch(err => {
        if (err && err.name === 'AbortError') return { cancelled: true };
        // Any option/type error falls back to <input accept="...">
        return this._askFileViaInput(accept);
      });
  }

  _askFilesViaFilePicker(accept) {
    const pickerTypes = this._buildPickerTypesFromAccept(accept);

    return window.showOpenFilePicker({
        multiple: true,
        types: pickerTypes.length ? pickerTypes : undefined,
        excludeAcceptAll: false
      })
      .then(async function onHandles(handles) {
        const files = await Promise.all(handles.map(function getFile(handle) {
          return handle.getFile();
        }));
        return { files };
      })
      .catch(function onError(err) {
        if (err && err.name === 'AbortError') return { cancelled: true };
        return this._askFileViaInput(accept, { multiple: true });
      }.bind(this));
  }

  // ---------- Private: <input type="file"> fallback ----------
  _askFileViaInput(accept, { multiple = false, timeoutMs = 20000 } = {}) {
    let resolve;
    const p = new Promise(_resolve => (resolve = _resolve));

    const input = document.createElement('input');
    input.type = 'file';
    if (accept) input.accept = accept;
    if (multiple) input.multiple = true;
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    input.style.top = '0';
    document.body.appendChild(input);

    let settled = false;

    function settle(val) {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(val);
    };

    function onChange() {
      const files = input.files;
      if (files && files.length) {
        if (multiple) {
          settle({ files: Array.from(files) });
        } else {
          settle({ file: files[0] });
        }
      } else {
        settle({ cancelled: true });
      }
    };

    // Some UIs don’t blur/focus the window; use both signals.
    let dialogLikelyClosed = false;

    function onFocus() {
      dialogLikelyClosed = true;
      maybeResolveCancelSoon();
    };

    function onVisibility() {
      if (!document.hidden) {
        dialogLikelyClosed = true;
        maybeResolveCancelSoon();
      }
    };

    function maybeResolveCancelSoon() {
      // Give the 'change' event a moment to fire first.
      setTimeout(() => {
        if (!settled && dialogLikelyClosed && (!input.files || input.files.length === 0)) {
          settle({ cancelled: true });
        }
      }, 300);
    };

    const timer = setTimeout(() => {
      if (!settled) settle({ cancelled: true, reason: 'timeout' });
    }, timeoutMs);

    function cleanup() {
      clearTimeout(timer);
      input.removeEventListener('change', onChange, false);
      window.removeEventListener('focus', onFocus, true);
      document.removeEventListener('visibilitychange', onVisibility, false);
      input.remove();
    }

    input.addEventListener('change', onChange, { once: true });
    window.addEventListener('focus', onFocus, true);
    document.addEventListener('visibilitychange', onVisibility, false);

    // Must be invoked synchronously inside the user's click/keypress handler.
    input.click();

    return p;
  }

  // ---------- Your existing helpers unchanged ----------
  askAnyTypeFile() {
    const type = [this.imageType, this.videoType, this.documentType, ...xlsxTypes].join(',');
    return this.askFile({ type });
  }

  askAnyTypeFiles() {
    const type = [this.imageType, this.videoType, this.documentType, ...xlsxTypes].join(',');
    return this.askFiles({ type, limit: 5 });
  }

  askDocumentFile() {
    const type = this.documentType;
    return this.askFile({ type });
  }

  askDocumentFiles(limit) {
    const type = this.documentType;
    return this.askFiles({ type, limit });
  }

  askVideoFile() {
    const type = this.videoType;
    return this.askFile({ type });
  }

  askImageFile() {
    const type = this.imageType;
    return this.askFile({ type });
  }

  askImageOrVideoFile() {
    const type = [this.imageType, this.videoType].join(',');
    return this.askFile({ type });
  }

  askAudioFile() {
    const type = this.audioType;
    return this.askFile({ type });
  }

  askCSVOrXLSXFile() {
    const type = [csvType, ...xlsxTypes].join(',');
    return this.askFile({ type });
  }

  askFileByTypes(types) {
    if (types.includes(fileTypes.ANY)) {
      return this.askAnyTypeFile();
    }

    const type = [];
    if (types.includes(fileTypes.IMAGE)) type.push(this.imageType);
    if (types.includes(fileTypes.VIDEO)) type.push(this.videoType);
    if (types.includes(fileTypes.AUDIO)) type.push('audio/*');
    if (types.includes(fileTypes.DOCUMENT)) type.push(this.documentType);
    if (types.includes(fileTypes.HTML)) type.push('text/html');
    if (types.includes(fileTypes.SHEETS)) type.push(...xlsxTypes, csvType);
    if (types.includes(fileTypes.CSV)) type.push(csvType);

    return this.askFile({ type: type.join(',') });
  }

  downloadTextAsFile(filename, text, contentType, ext) {
    var dataStr = `data:${contentType};charset=utf-8,` + encodeURIComponent(text);
    var downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute('href', dataStr);
    downloadAnchorNode.setAttribute('download', filename + '.' + ext);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  }

  isMobile() {
    return window.innerWidth <= generalConstants.MOBILE_SCREEN_WIDTH;
  }

  determineFileType(file) {
    const mimetype = file.mimetype;
    if (imageTypes.includes(mimetype)) return fileTypes.IMAGE;
    if (videoTypes.includes(mimetype)) return fileTypes.VIDEO;
    if (documentTypes.includes(mimetype)) return fileTypes.DOCUMENT;
    if (mimetype === csvType) return fileTypes.CSV;
    return fileTypes.UNKNOWN;
  }

  downloadURLAsFile(url) {
    const a = document.createElement('a');
    a.href = url.toString();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  downloadSVGStringAsFile(filename, string) {
    const svgBlob = new Blob([string], { type: 'image/svg+xml' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = svgUrl;
    downloadLink.download = filename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(svgUrl);
  }

  downloadDataURLAsFile(name, dataURL) {
    const a = document.createElement('a');
    a.href = dataURL;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  determineLangaugeDetails() {
    const lang = navigator.language;
    const rtlLangs = ['ar', 'he', 'fa', 'ur'];
    const dir = rtlLangs.some(code => lang.startsWith(code)) ? 'rtl' : 'ltr';
    return { lang, dir };
  }
}

export default DOMUtility;